import { forwardRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import Card from '@mui/material/Card';

import { useIntl } from 'react-intl';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { IconButton, InputAdornment, useMediaQuery, useTheme } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import PersonIcon from '@mui/icons-material/Person';
import KeyIcon from '@mui/icons-material/Key';
import { ThemeProvider } from '@mui/material/styles';
import * as PropTypes from 'prop-types';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import BasicLayout from 'pages/authentication/components/BasicLayout';
import { useSignInMutation } from 'services/authApi';
import { fErrors } from 'utils/formatError';
import { useNotification } from 'hook/useNotification';
import { setCredentials } from 'slice/authSlice';
import { isAuthorized } from 'utils/userUtils';
import { getBgSignIn } from 'utils/urlUtils';
import InputWithStartAdornment from 'components/inputs/InputWithStartAdornment';
import { useThemeBuilder } from 'hook/useThemeBuilder';
import { useMaterialUIController } from 'context';
import logoAi from 'assets/images/logo-dina.svg';

const PasswordWithStartAdornment = forwardRef((props, ref) => {
  const { formatMessage: msg } = useIntl();

  const [passwordVisible, setPasswordVisible] = useState(false);

  const onPasswordVisibleClick = () => {
    setPasswordVisible(!passwordVisible);
  };

  const onPasswordMouseDown = (event) => {
    event.preventDefault();
  };

  return (
    <InputWithStartAdornment
      ref={ref}
      type={passwordVisible ? 'text' : 'password'}
      fullWidth
      autoComplete="current-password"
      adornmentIcon={<KeyIcon fontSize="medium" />}
      label={props.label}
      error={!!props.errors.password}
      helperText={props.errors?.password?.message}
      endAdornment={
        <InputAdornment position="end">
          <IconButton
            aria-label={msg({ id: 'common.toggle.password' })}
            onClick={onPasswordVisibleClick}
            onMouseDown={onPasswordMouseDown}
            edge="end"
          >
            {passwordVisible ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      }
      {...props}
    />
  );
});

PasswordWithStartAdornment.propTypes = {
  passwordVisible: PropTypes.bool,
  label: PropTypes.string,
  errors: PropTypes.any,
  register: PropTypes.any
};

function AuthenticationCard() {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const validationSchema = Yup.object().shape({
    login: Yup.string().required(msg({ id: 'signin.login.required' })),
    password: Yup.string().required(msg({ id: 'signin.password.required' }))
  });

  const {
    handleSubmit,
    formState: { errors },
    register
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const [signIn, { isLoading }] = useSignInMutation();

  function haveRightToConnect(user) {
    return isAuthorized(user, RoleEnum.ADMIN) || isAuthorized(user, RoleEnum.ASSISTANT);
  }

  const onSubmit = (credentials) => {
    signIn(credentials)
      .unwrap()
      .then((user) => {
        if (!haveRightToConnect(user)) {
          addNotification({
            type: 'warning',
            title: msg({ id: 'notification.warning.title' }),
            message: msg({ id: 'security.insufficientPrivileges' })
          });
          return;
        }

        dispatch(setCredentials(user));
        // Send them back to the page they tried to visit when they were
        // redirected to the login page. Use { replace: true } so we don't create
        // another entry in the history stack for the login page.  This means that
        // when they get to the protected page and click the back button, they
        // won't end up back on the login page, which is also really nice for the
        // user experience.
        navigate(from, { replace: true });
      })
      .catch((errors) => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: fErrors(msg, errors)
        });
      });
  };

  return (
    <Card
      id="loginCard"
      variant="outlined"
      sx={{
        height: { lg: '100%' },
        minWidth: '500px',
        m: 2,
        background: 'rgba(172, 172, 196, 0.11)',
        backdropFilter: 'blur(30px)',
        borderColor: (theme) => `${theme.palette.primary.main} !important`
      }}
    >
      <MDBox
        bgColor="none"
        borderRadius="lg"
        coloredShadow="none"
        mx={2}
        mt={0}
        p={2}
        mb={1}
        textAlign="center"
      >
        <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
          <MDBox
            component="img"
            src={logoAi}
            alt="Logo"
            sx={{
              maxWidth: '400px',
              maxHeight: '180px',
              borderRadius: '3rem'
            }}
          />
        </MDTypography>
      </MDBox>
      <MDBox pt={4} pb={3} px={3}>
        <MDBox
          component="form"
          role="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            '& input:-internal-autofill-selected, & input:-webkit-autofill, & input:-webkit-autofill:focus':
              {
                transition: 'background-color 600000s 0s, color 600000s 0s'
              }
          }}
        >
          <MDBox mb={2}>
            <InputWithStartAdornment
              autoFocus
              fullWidth
              autoComplete="current-login"
              adornmentIcon={<PersonIcon fontSize="medium" />}
              label={msg({ id: 'signin.login' })}
              error={!!errors.login}
              helperText={errors?.login?.message}
              {...register('login')}
            />
          </MDBox>
          <MDBox mb={2}>
            <PasswordWithStartAdornment
              label={msg({ id: 'signin.password' })}
              errors={errors}
              {...register('password')}
            />
          </MDBox>
          <MDBox mt={4} mb={1}>
            <MDButton
              variant="gradient"
              color="primary"
              fullWidth
              type="submit"
              loading={isLoading}
            >
              {msg({ id: 'action.signin' })}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export const RoleEnum = {
  ADMIN: 'ADMIN',
  USER: 'USER',
  ASSISTANT: 'ASSISTANT'
};

AuthenticationCard.propTypes = {
  theme: PropTypes.any
};

function Basic() {
  const { formatMessage: msg } = useIntl();
  const theme = useTheme();
  const lgWidthDown = useMediaQuery(theme.breakpoints.down('lg'));

  const { controller } = useMaterialUIController();
  const { primaryColor, secondaryColor } = controller;
  const { darkTheme } = useThemeBuilder(primaryColor, secondaryColor);

  return (
    <ThemeProvider theme={darkTheme}>
      <BasicLayout
        id="loginLayout"
        imageURL={getBgSignIn(Math.floor(Math.random() * 6))}
        title={msg({ id: 'signin.title' })}
        justifyContent={lgWidthDown ? 'center' : 'flex-end'}
        height={{ lg: '60%' }}
      >
        <AuthenticationCard theme={darkTheme} />
      </BasicLayout>
    </ThemeProvider>
  );
}

export default Basic;
